import { REGISTRATIONS_PERMISSIONS } from '@constants/registrations-permissions.constant';
import { EUserProfileIds, type IUser } from '@interfaces/user';
import { hasAssociatedUserProfile } from '@utils/client-area-permissions.util';

function hasPermission(user: IUser | null | undefined, permission: string) {
	return user?.profilePermissions?.includes(permission) ?? false;
}

function isSellerOrAdmin(user?: IUser | null): boolean {
	return (
		user?.profileId === EUserProfileIds.SELLER ||
		user?.profileId === EUserProfileIds.APPLICATION_ADMIN
	);
}

/**
 * Matches API: legacy tenants and users without a granular profile stay allowed;
 * otherwise the profile permission is required.
 */
function allowsRegistrationsAction(
	user: IUser | null | undefined,
	permission: string,
	options: { useLegacyPermissions: boolean }
): boolean {
	if (!user) return false;
	if (options.useLegacyPermissions || !hasAssociatedUserProfile(user)) {
		return true;
	}
	return hasPermission(user, permission);
}

export function canCreateClients(
	user: IUser | null | undefined,
	options: { useLegacyPermissions: boolean }
): boolean {
	return allowsRegistrationsAction(
		user,
		REGISTRATIONS_PERMISSIONS.createClients,
		options
	);
}

export function canEditClients(
	user: IUser | null | undefined,
	options: { useLegacyPermissions: boolean }
): boolean {
	return allowsRegistrationsAction(
		user,
		REGISTRATIONS_PERMISSIONS.editClients,
		options
	);
}

/**
 * Seller-order / portal-orders context: role + feature first, then createClients.
 */
export function canCreatePortalClients(
	user: IUser | null | undefined,
	options: {
		portalOrdersEnabled: boolean;
		useLegacyPermissions: boolean;
	}
): boolean {
	if (!user || !options.portalOrdersEnabled || !isSellerOrAdmin(user)) {
		return false;
	}
	return canCreateClients(user, {
		useLegacyPermissions: options.useLegacyPermissions,
	});
}

/**
 * Whether the "new client" action should be shown in seller-order.
 * Permission is checked on click so the user can be notified.
 */
export function canShowCreatePortalClient(
	user: IUser | null | undefined,
	options: { portalOrdersEnabled: boolean }
): boolean {
	return Boolean(user && options.portalOrdersEnabled && isSellerOrAdmin(user));
}
